import type { ThunkAction } from "@reduxjs/toolkit";

import type { ReduxState } from "@dashboard/types/reduxState";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import type { MenuUrlV2ReducerAction } from "../reducers/menuUrlV2Reducer";

export type AssignmentOp = "add" | "remove";

export type AssignmentResult = { ok: true } | { ok: false; error: unknown };

/**
 * Applies a single "add menu to URL" or "remove menu from URL" delta to one
 * MenuUrlV2 document. Uses the CURRENT Redux snapshot as the source of truth
 * for the array to mutate.
 *
 * Returns a result object rather than throwing: the save orchestrator needs
 * to know which specific URLs failed, not abort on first error.
 */
export const applyUrlAssignmentDelta =
  (
    urlDocId: string,
    menuId: string,
    op: AssignmentOp
  ): ThunkAction<
    Promise<AssignmentResult>,
    ReduxState,
    ThunkExtraArgument,
    MenuUrlV2ReducerAction
  > =>
  async (_dispatch, getState, { menuUrlV2Api }) => {
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return { ok: false, error: new Error("No restaurant selected") };
    }
    const url = getState().menuUrlV2.menuUrlsV2.find(
      (u) => u.docId === urlDocId
    );
    if (!url) {
      return { ok: false, error: new Error("URL not found in state") };
    }

    const current = url.menuIdsInDisplayOrder;
    const next =
      op === "add"
        ? current.includes(menuId)
          ? current
          : [...current, menuId]
        : current.filter((id) => id !== menuId);

    if (arraysEqual(next, current)) {
      return { ok: true };
    }

    try {
      await menuUrlV2Api.updateMenuUrlV2(currentRest.uuid, urlDocId, {
        menuIdsInDisplayOrder: next,
      });
      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    }
  };

function arraysEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
